import type { BillablePrice, CreatePriceRequest } from '@domains/subscription/priv/Billables/api/api'
import {
  type AssignedPriceMapper,
  mapAssignedPaymentFrequency,
  mapPaymentFrequencyToPriceFrequency,
  type PriceRequestMapper,
} from '@domains/subscription/priv/Billables/api/mappers'
import type { BaseAlgorithm } from '@domains/subscription/priv/components/Confirmable/confirmable'
import type {
  AssignedPriceAlgorithm,
  MaxAlgorithm,
  MinAlgorithm,
  PriceRequest,
  TemplateAssignedPrice,
  TemplatePriceRequest,
} from '@mynt/backoffice-api-spec/billables'
import type { MinPrice, MinPriceRequest } from './min'

export const isMin = <T extends BaseAlgorithm>(billable: unknown): billable is MinPrice<T> => {
  return (billable as MinPrice<T>)?.type === 'Min'
}

export const isMinAssignedPrice = (price: TemplateAssignedPrice): price is MinAssignedPrice => {
  return price.algorithm.type === 'Min'
}

export const mapBillableToMinPrice = <T extends BillablePrice>(billable: MinPrice<T>): MinPriceRequest<T> => {
  return {
    billableId: billable.billableId,
    type: 'Min',
    lalgorithm: billable.lalgorithm,
    ralgorithm: billable.ralgorithm,
    paymentFrequency: billable.paymentFrequency,
    supported: true,
  }
}

export class MinPriceRequestMapper<T extends CreatePriceRequest> implements PriceRequestMapper {
  supports(price: CreatePriceRequest): price is MinPriceRequest<T> {
    return price.type === 'Min'
  }

  toAssignedPriceAlgorithm(price: CreatePriceRequest, mapper: (price: CreatePriceRequest) => PriceRequestMapper): AssignedPriceAlgorithm {
    if (!this.supports(price)) {
      throw new Error(`Unsupported price request type in MinPriceRequestMapper:toAssignedPriceAlgorithm: ${price.type}`)
    }

    if (!price.lalgorithm || !price.ralgorithm) {
      throw new Error(`Missing left or right algorithm in MinPriceRequestMapper:toAssignedPriceAlgorithm`)
    }

    const leftMapper = mapper(price.lalgorithm)
    const rightMapper = mapper(price.ralgorithm)

    if (!leftMapper.supports(price.lalgorithm)) {
      throw new Error(`Unsupported left algorithm type in MinPriceRequestMapper:toAssignedPriceAlgorithm: ${price.lalgorithm.type}`)
    }

    if (!rightMapper.supports(price.ralgorithm)) {
      throw new Error(`Unsupported right algorithm type in MinPriceRequestMapper:toAssignedPriceAlgorithm: ${price.ralgorithm.type}`)
    }

    return {
      type: 'Min',
      lalgorithm: leftMapper.toAssignedPriceAlgorithm(price.lalgorithm, mapper),
      ralgorithm: rightMapper.toAssignedPriceAlgorithm(price.ralgorithm, mapper),
    }
  }

  toPriceRequestDto(price: CreatePriceRequest, algorithmMapper: (price: CreatePriceRequest) => PriceRequestMapper): TemplatePriceRequest {
    if (!this.supports(price)) {
      throw new Error(`Unsupported price request type in MinPriceRequestMapper:toPriceRequestDto: ${price.type}`)
    }

    if (!price.lalgorithm || !price.ralgorithm) {
      throw new Error(`Missing left or right algorithm in MinPriceRequestMapper:toPriceRequestDto`)
    }

    const leftMapper = algorithmMapper(price.lalgorithm)
    const rightMapper = algorithmMapper(price.ralgorithm)

    if (!leftMapper.supports(price.lalgorithm)) {
      throw new Error(`Unsupported left algorithm type in MinPriceRequestMapper:toPriceRequestDto: ${price.lalgorithm.type}`)
    }

    if (!rightMapper.supports(price.ralgorithm)) {
      throw new Error(`Unsupported right algorithm type in MinPriceRequestMapper:toPriceRequestDto: ${price.ralgorithm.type}`)
    }

    return {
      algorithm: {
        type: 'Min',
        lalgorithm: leftMapper.toAssignedPriceAlgorithm(price.lalgorithm, algorithmMapper),
        ralgorithm: rightMapper.toAssignedPriceAlgorithm(price.ralgorithm, algorithmMapper),
      },
      priceFrequency: mapPaymentFrequencyToPriceFrequency(price.paymentFrequency),
    }
  }
}

type MinAssignedPrice = TemplateAssignedPrice & {
  algorithm: MinAlgorithm
}

export class MinAssignedPriceMapper implements AssignedPriceMapper {
  supports(assignedPrice: TemplateAssignedPrice) {
    return isMinAssignedPrice(assignedPrice)
  }

  toBillablePrice(assignedPrice: TemplateAssignedPrice): MinPrice<BaseAlgorithm> {
    if (!isMinAssignedPrice(assignedPrice)) {
      throw new Error(`Unsupported assigned price type in MinAssignedPriceMapper: ${assignedPrice.algorithm.type}`)
    }

    return {
      billableId: assignedPrice.billableId,
      type: 'Min',
      lalgorithm: assignedPrice.algorithm.lalgorithm,
      ralgorithm: assignedPrice.algorithm.ralgorithm,
      paymentFrequency: mapAssignedPaymentFrequency(assignedPrice.priceFrequency),
      supported: true,
    }
  }
}
